import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Checkbox } from "@/components/admin/AdminFields";
import {
  IconChevronDown,
  IconDownload,
  IconGripVertical,
} from "@/components/icons";
import { useClickOutside } from "@/hooks/useClickOutside";
import type {
  SubmissionsExportColumns,
  SubmissionsExportFieldColumn,
  SubmissionsExportFormat,
  SubmissionsExportMetaKey,
} from "@/types/submission";
import { staffColumnExportLabel } from "@/lib/forms/staffColumns";

export type { SubmissionsExportFormat };

const FORMAT_TABS: Array<{ id: SubmissionsExportFormat; label: string }> = [
  { id: "xlsx", label: "XLSX" },
  { id: "csv", label: "CSV" },
  { id: "docx", label: "Word" },
];

function mergeOrderedIds(previous: number[], catalogIds: number[]): number[] {
  const known = new Set(catalogIds);
  const kept = previous.filter((id) => known.has(id));
  const missing = catalogIds.filter((id) => !kept.includes(id));
  return [...kept, ...missing];
}

function SortableExportField({
  field,
  idPrefix,
  checked,
  onToggle,
}: {
  field: SubmissionsExportFieldColumn;
  idPrefix: string;
  checked: boolean;
  onToggle: (fieldId: number, checked: boolean) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={[
        "flex items-center gap-1 rounded-md py-0.5",
        isDragging ? "z-10 bg-white shadow-sm" : "",
      ].join(" ")}
    >
      <button
        type="button"
        className="inline-flex h-7 w-6 shrink-0 cursor-grab items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600 active:cursor-grabbing"
        aria-label={`Перетащить «${field.label}»`}
        {...attributes}
        {...listeners}
      >
        <IconGripVertical size={14} />
      </button>
      <Checkbox
        id={`${idPrefix}-field-${field.id}`}
        size="compact"
        checked={checked}
        onChange={(nextChecked) => onToggle(field.id, nextChecked)}
        label={field.label}
      />
    </div>
  );
}

export function SubmissionsExportMenu({
  idPrefix,
  format,
  onFormatChange,
  columns,
  selectedMetaKeys,
  selectedFieldIds,
  selectedStaffColumnIds,
  onToggleMeta,
  onToggleField,
  onToggleStaffColumn,
  onSelectAll,
  onClearAll,
  selectedCount,
  totalCount,
  allSelected,
  exporting,
  downloadDisabled,
  onDownload,
  hint,
  toolbarExtra,
  children,
}: {
  idPrefix: string;
  format: SubmissionsExportFormat;
  onFormatChange: (format: SubmissionsExportFormat) => void;
  columns: SubmissionsExportColumns;
  selectedMetaKeys: Set<SubmissionsExportMetaKey>;
  selectedFieldIds: Set<number>;
  selectedStaffColumnIds?: Set<number>;
  onToggleMeta: (key: SubmissionsExportMetaKey, checked: boolean) => void;
  onToggleField: (fieldId: number, checked: boolean) => void;
  onToggleStaffColumn?: (columnId: number, checked: boolean) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
  selectedCount: number;
  totalCount: number;
  allSelected: boolean;
  exporting: boolean;
  downloadDisabled: boolean;
  onDownload: (orderedFieldIds: number[]) => void;
  hint?: string;
  toolbarExtra?: (orderedFieldIds: number[]) => ReactNode;
  children?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useClickOutside(containerRef, close, open);

  const catalogIds = useMemo(
    () => columns.fields.map((field) => field.id),
    [columns.fields],
  );
  const catalogKey = catalogIds.join(",");
  const [orderedFieldIds, setOrderedFieldIds] = useState<number[]>(catalogIds);

  useEffect(() => {
    const nextIds = catalogKey
      ? catalogKey.split(",").map((value) => Number(value))
      : [];
    setOrderedFieldIds((previous) => mergeOrderedIds(previous, nextIds));
  }, [catalogKey]);

  const fieldsById = useMemo(() => {
    const map = new Map(columns.fields.map((field) => [field.id, field]));
    return map;
  }, [columns.fields]);

  const orderedFields = useMemo(
    () =>
      orderedFieldIds
        .map((id) => fieldsById.get(id))
        .filter(
          (field): field is SubmissionsExportFieldColumn => field != null,
        ),
    [fieldsById, orderedFieldIds],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setOrderedFieldIds((current) => {
      const from = current.indexOf(Number(active.id));
      const to = current.indexOf(Number(over.id));
      if (from < 0 || to < 0) return current;
      return arrayMove(current, from, to);
    });
  };

  const formatLabel = format === "docx" ? "Word" : format.toUpperCase();

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [close, open]);

  useLayoutEffect(() => {
    if (!open) return;

    const placeMenu = () => {
      const trigger = triggerRef.current;
      const menu = menuRef.current;
      if (!trigger || !menu) return;

      const rect = trigger.getBoundingClientRect();
      const margin = 8;
      const gap = 6;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const left = Math.max(margin, rect.left);
      const width = Math.min(512, vw - margin - left);

      const spaceBelow = vh - rect.bottom - gap - margin;
      const spaceAbove = rect.top - gap - margin;
      const placeBelow = spaceBelow >= 220 || spaceBelow >= spaceAbove;
      const maxHeight = Math.max(160, placeBelow ? spaceBelow : spaceAbove);

      menu.style.width = `${width}px`;
      menu.style.maxHeight = `${maxHeight}px`;
      menu.style.left = `${left}px`;
      if (placeBelow) {
        menu.style.top = `${rect.bottom + gap}px`;
        menu.style.bottom = "auto";
      } else {
        menu.style.top = "auto";
        menu.style.bottom = `${vh - rect.top + gap}px`;
      }
      menu.style.visibility = "visible";
    };

    placeMenu();
    window.addEventListener("resize", placeMenu);
    window.addEventListener("scroll", placeMenu, true);
    return () => {
      window.removeEventListener("resize", placeMenu);
      window.removeEventListener("scroll", placeMenu, true);
    };
  }, [open, columns, format, selectedCount, totalCount, orderedFieldIds]);

  return (
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        {children}
        <div ref={containerRef} className="relative flex items-center gap-1.5">
          <button
            ref={triggerRef}
            type="button"
            aria-expanded={open}
            aria-haspopup="dialog"
            aria-controls={`${idPrefix}-menu`}
            onClick={() => setOpen((current) => !current)}
            className="inline-flex h-8 items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 font-onest text-xs font-semibold uppercase tracking-wide text-slate-700 shadow-sm hover:border-slate-300 hover:bg-slate-50"
          >
            {formatLabel}
            <IconChevronDown
              size={14}
              className={[
                "text-slate-400 transition-transform",
                open ? "rotate-180" : "",
              ].join(" ")}
            />
          </button>

          <button
            type="button"
            disabled={downloadDisabled || exporting}
            onClick={() => onDownload(orderedFieldIds)}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-brand-500 px-2.5 font-onest text-xs font-medium text-white shadow-sm shadow-brand-500/20 hover:bg-brand-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <IconDownload size={14} />
            {exporting ? "Скачивание…" : "Скачать"}
          </button>

          {open ? (
            <div
              ref={menuRef}
              id={`${idPrefix}-menu`}
              role="dialog"
              aria-label="Настройки экспорта"
              className="fixed z-50 flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
              style={{ visibility: "hidden" }}
            >
              <div className="shrink-0 border-b border-slate-100 p-3">
                <div
                  role="tablist"
                  aria-label="Формат файла"
                  className="flex gap-1 rounded-lg border border-slate-200 bg-slate-50/80 p-1"
                >
                  {FORMAT_TABS.map((tab) => {
                    const selected = format === tab.id;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={selected}
                        onClick={() => onFormatChange(tab.id)}
                        className={[
                          "flex-1 rounded-md px-3 py-1.5 font-onest text-xs font-semibold uppercase tracking-wide transition-colors",
                          selected
                            ? "bg-white text-ink shadow-sm"
                            : "text-slate-500 hover:text-slate-800",
                        ].join(" ")}
                      >
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto p-3">
                <div className="mb-2 flex flex-wrap items-center gap-3">
                  <p className="font-onest text-xs font-semibold text-ink">
                    Поля для экспорта
                  </p>
                  <button
                    type="button"
                    onClick={onSelectAll}
                    className="font-onest text-[11px] font-medium text-brand-700 hover:underline"
                  >
                    Выбрать все
                  </button>
                  <button
                    type="button"
                    onClick={onClearAll}
                    className="font-onest text-[11px] font-medium text-slate-500 hover:underline"
                  >
                    Снять все
                  </button>
                  {!allSelected ? (
                    <span className="font-onest text-[11px] text-slate-400">
                      {selectedCount}/{totalCount}
                    </span>
                  ) : null}
                </div>

                {columns.meta.length > 0 ? (
                  <div className="mb-3">
                    <p className="mb-2 font-onest text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      Служебные
                    </p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {columns.meta.map((column) => (
                        <Checkbox
                          key={column.key}
                          id={`${idPrefix}-meta-${column.key}`}
                          size="compact"
                          checked={selectedMetaKeys.has(column.key)}
                          onChange={(checked) =>
                            onToggleMeta(column.key, checked)
                          }
                          label={column.label}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}

                {(columns.staffColumns ?? []).length > 0 &&
                selectedStaffColumnIds &&
                onToggleStaffColumn ? (
                  <div className="mt-3">
                    <p className="mb-2 font-onest text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      Пометки
                    </p>
                    <div className="flex flex-col gap-1">
                      {(columns.staffColumns ?? []).map((column) => (
                        <Checkbox
                          key={column.id}
                          id={`${idPrefix}-staff-${column.id}`}
                          size="compact"
                          checked={selectedStaffColumnIds.has(column.id)}
                          onChange={(checked) =>
                            onToggleStaffColumn(column.id, checked)
                          }
                          label={staffColumnExportLabel(column)}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}

                {orderedFields.length > 0 ? (
                  <div>
                    {columns.meta.length > 0 ? (
                      <p className="mb-2 font-onest text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Поля формы
                      </p>
                    ) : null}
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={orderedFieldIds}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="flex flex-col gap-1">
                          {orderedFields.map((field) => (
                            <SortableExportField
                              key={field.id}
                              field={field}
                              idPrefix={idPrefix}
                              checked={selectedFieldIds.has(field.id)}
                              onToggle={onToggleField}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  </div>
                ) : null}

                {hint ? (
                  <p className="mt-3 font-onest text-[11px] text-slate-500">
                    {hint}
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
        {toolbarExtra ? toolbarExtra(orderedFieldIds) : null}
      </div>
    </div>
  );
}
